import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SectionHeading, MandirCard, MuhuratCard, StatsSection, TestimonialsSection } from "@/components/Cards";
import PanchangCard from "@/components/PanchangCard";
import { Flame, CalendarDays, Sparkles, ArrowRight, Star, Smartphone, Search, ChevronRight, ScrollText } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1575989762363-c7ce0137427a?crop=entropy&cs=srgb&fm=jpg&q=85";
const SERVICES = [
  { icon: CalendarDays, slug: "panchang", title: "Vedic Panchang", desc: "Daily Tithi, Nakshatra & auspicious muhurat", to: "/vedic-services/panchang" },
  { icon: Star, slug: "kundli-rashi-dosha", title: "Kundli, Rashi & Dosha", desc: "Birth chart, Rashi insights & dosha remedies", to: "/vedic-services/kundli" },
  { icon: Flame, slug: "matchmaking", title: "Kundli Matchmaking", desc: "Ashta Koota Guna Milan for marriage", to: "/vedic-services/matchmaking" },
];

export default function Home() {
  const [panchang, setPanchang] = useState(null);
  const [festivals, setFestivals] = useState([]);
  const [mandirs, setMandirs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/panchang/today").then((r) => setPanchang(r.data));
    api.get("/festivals").then((r) => setFestivals(r.data.slice(0, 6)));
    api.get("/mandirs").then((r) => setMandirs(r.data.filter((m) => m.featured).slice(0, 3)));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
    api.get("/blogs").then((r) => setBlogs(r.data.slice(0, 3)));
    api.get("/stats").then((r) => setStats(r.data));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/92 to-background/40 dark:from-background dark:via-background/90" />
        </div>
        <div className="relative px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-float-up">
            <p className="font-devanagari text-accent text-lg mb-3">॥ ॐ नमः शिवाय ॥</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight leading-[1.1]">
              Connect with your <span className="text-gold-gradient">Mandir</span>, book worship with devotion
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Discover sacred temples across Bharat and book authentic Worships & Vedic Rituals online, performed by verified Purohits — from the comfort of your home.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/divine-bless"><Button data-testid="hero-book-worship" className="rounded-full h-12 px-7 bg-primary hover:bg-primary/90 text-primary-foreground text-base">Book Worship <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
              <Link to="/mandirs"><Button data-testid="hero-explore-mandirs" variant="outline" className="rounded-full h-12 px-7 border-accent text-foreground hover:bg-accent hover:text-accent-foreground text-base">Explore Mandirs</Button></Link>
            </div>
            <div className="mt-4">
              <Link to="/vedic-services/panchang?view=month"><Button data-testid="hero-monthly-panchang" variant="ghost" className="rounded-full h-11 px-6 text-primary hover:bg-accent/15 text-sm font-semibold"><CalendarDays className="mr-1.5 h-4 w-4" /> View Full Vedic Panchang (Monthly) <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-6 text-sm">
              {stats && [["Mandirs", stats.mandirs + "+"], ["Verified Purohits", stats.purohits + "+"], ["Bookings", (stats.bookings/1000).toFixed(0) + "K+"]].map(([l, v]) => (
                <div key={l}><p className="font-serif text-2xl font-bold text-primary">{v}</p><p className="text-muted-foreground text-xs uppercase tracking-wide">{l}</p></div>
              ))}
            </div>
          </div>
          <div className="animate-float-up" style={{ animationDelay: "0.15s" }}>
            <PanchangCard p={panchang} />
          </div>
        </div>
      </section>

      {/* Festivals */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20">
        <SectionHeading eyebrow="Shubh Muhurat" title="Upcoming Festivals & Vrat Muhurtam" subtitle="Auspicious timings for Mundan, Upnayan, Grih-Pravesh, Vivah, Vrat and more." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {festivals.map((f) => <MuhuratCard key={f.id} item={f} kind="festival" />)}
        </div>
      </section>

      {/* Vedic services overview */}
      <section className="bg-secondary/40 border-y border-border">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20">
          <SectionHeading eyebrow="Vedic Wisdom" title="Explore our Vedic Services" center subtitle="Ancient knowledge to guide your path — Panchang, Kundli, Dosha remedies and marriage matchmaking." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s) => (
              <Link to={s.to} key={s.title} data-testid={`vedic-overview-${s.slug}`} className="rounded-2xl bg-card border border-border p-6 hover-lift hover:border-accent group">
                <span className="h-12 w-12 grid place-items-center rounded-xl bg-gradient-to-br from-accent/20 to-primary/20 text-primary mb-4"><s.icon className="h-6 w-6" /></span>
                <h3 className="font-serif text-lg font-semibold">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-1.5">{s.desc}</p>
                <span className="inline-flex items-center gap-1 text-sm text-primary mt-3 group-hover:gap-2 transition-all">Learn more <ChevronRight className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured mandirs */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20">
        <div className="flex items-end justify-between mb-10">
          <SectionHeading eyebrow="Divya Sthan" title="Featured Mandirs" subtitle="Discover the sacred temples of Bharat and book your worship." />
          <Link to="/mandirs" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {mandirs.map((m) => <MandirCard key={m.id} m={m} />)}
        </div>
      </section>

      <StatsSection stats={stats} />

      {/* Booking CTA */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pb-4">
        <div className="grid md:grid-cols-2 gap-5">
          {[{ t: "Book Worship", d: "Rudrabhishek, Ganesh Vandana, Satyanarayan & more", img: "https://images.unsplash.com/photo-1605302977545-3a09913be1dd?crop=entropy&cs=srgb&fm=jpg&q=85", to: "/divine-bless" },
            { t: "Book Vedic Rituals", d: "Grih Pravesh, Vivah, Mundan, Upnayan sanskars", img: "https://images.unsplash.com/photo-1662852744966-143f1dde2634?crop=entropy&cs=srgb&fm=jpg&q=85", to: "/divine-bless" }].map((c) => (
            <Link to={c.to} key={c.t} className="relative rounded-2xl overflow-hidden h-56 group border border-border">
              <img src={c.img} alt="" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-black/20" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <h3 className="font-serif text-2xl font-bold">{c.t}</h3>
                <p className="text-white/85 text-sm mt-1">{c.d}</p>
                <span className="inline-flex items-center gap-1.5 mt-3 text-accent font-semibold">Start booking <ArrowRight className="h-4 w-4" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Blog */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20">
        <SectionHeading eyebrow="Gyan" title="From the Vedic Blog" subtitle="Stories, traditions and wisdom from our purohits." />
        <div className="grid sm:grid-cols-3 gap-6">
          {blogs.map((b) => (
            <Link to={`/blog/${b.slug}`} key={b.id} data-testid={`home-blog-${b.id}`} className="rounded-2xl bg-card border border-border overflow-hidden hover-lift hover:border-accent group">
              <div className="h-40 overflow-hidden"><img src={b.image} alt="" className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" /></div>
              <div className="p-5">
                <span className="text-xs text-accent font-medium flex items-center gap-1"><ScrollText className="h-3.5 w-3.5" /> {b.category}</span>
                <h3 className="font-serif text-lg font-semibold mt-1.5 leading-tight">{b.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{b.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsSection testimonials={testimonials} />

      {/* App download */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20">
        <div className="rounded-3xl bg-gradient-to-br from-primary to-[#8e2d16] text-primary-foreground p-8 sm:p-12 flex flex-col md:flex-row items-center justify-between gap-6 overflow-hidden relative">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
          <div className="relative">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold">Take your devotion everywhere</h2>
            <p className="mt-2 text-primary-foreground/90 max-w-lg">Get the Pavitra Mantra app for daily Panchang alerts, one-tap bookings and live puja updates.</p>
          </div>
          <div className="flex gap-3 relative">
            <Button data-testid="app-download-btn" className="rounded-full h-12 px-6 bg-white text-primary hover:bg-white/90"><Smartphone className="mr-2 h-4 w-4" /> Download App</Button>
          </div>
        </div>
      </section>
    </div>
  );
}
